import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'
import { getBelt, getBeltTheme, isChampion, calcRating, calcCatScore, makeAvatarSvg, makeInitialsSvg, getFlagSvg, getBeltLevel, getStripeCount } from '../lib/helpers'
import { getFighterPoints } from '../lib/points'
import { FRONT_STAT_KEYS } from '../lib/constants'
import PhotoUpload from '../components/PhotoUpload'
import ShareCard from '../components/ShareCard'

// Military stencil font
const STENCIL_FONT = "'VT323', monospace"

export default function MyCardPage() {
  const { user, profile, club, signOut } = useAuth()
  const [fighter, setFighter] = useState(null)
  const [skills, setSkills] = useState([])
  const [competitions, setCompetitions] = useState([])
  const [categories, setCategories] = useState([])
  const [techniques, setTechniques] = useState([])
  const [flipped, setFlipped] = useState(false)
  const [sharing, setSharing] = useState(false)
  const [loading, setLoading] = useState(true)
  const [recentCheckins, setRecentCheckins] = useState([])
  const [rankRequestSent, setRankRequestSent] = useState(false)
  const [badges, setBadges] = useState([])
  const [totalPoints, setTotalPoints] = useState(0)
  const [pinSession, setPinSession] = useState(null)
  const [cardClub, setCardClub] = useState(null)

  useEffect(() => {
    async function load() {
      const pinData = localStorage.getItem('fighter_session')
      if (pinData) {
        const session = JSON.parse(pinData)
        if (session.expires > Date.now()) {
          setPinSession(session)
          const fighterId = session.fighter_id
          const [f, sk, comps, cats, techs, cis] = await Promise.all([
            supabase.from('fighters').select('*').eq('id', fighterId).single(),
            supabase.from('fighter_skills').select('*').eq('fighter_id', fighterId),
            supabase.from('competitions').select('*').eq('fighter_id', fighterId).order('competition_date', { ascending: false }),
            supabase.from('skill_categories').select('*').eq('club_id', session.club_id).order('display_order'),
            supabase.from('clubs').select('*').eq('id', session.club_id).single(),
            supabase.from('techniques').select('*').order('display_order'),
            supabase.from('checkins').select('*, classes(*)').eq('fighter_id', fighterId).order('checked_in_at', { ascending: false }).limit(5),
          ])
          setFighter(f.data)
          setSkills(sk.data || [])
          setCompetitions(comps.data || [])
          setCategories(cats.data || [])
          setTechniques(techs.data || [])
          setRecentCheckins(cis.data || [])
          setCardClub(cis.data ? arguments[0] : null)
          setLoading(false)
          return
        } else {
          localStorage.removeItem('fighter_session')
        }
      }

      if (!profile) { setLoading(false); return }
      const { data: prof } = await supabase
        .from('profiles').select('*, fighters(*)').eq('id', user.id).single()
      if (!prof?.fighter_id) { setLoading(false); return }
      const fighterId = prof.fighter_id
      const [sk, comps, cats, techs, cis, clubRes] = await Promise.all([
        supabase.from('fighter_skills').select('*').eq('fighter_id', fighterId),
        supabase.from('competitions').select('*').eq('fighter_id', fighterId).order('competition_date', { ascending: false }),
        supabase.from('skill_categories').select('*').eq('club_id', prof.fighters.club_id).order('display_order'),
        supabase.from('techniques').select('*').order('display_order'),
        supabase.from('checkins').select('*, classes(*)').eq('fighter_id', fighterId).order('checked_in_at', { ascending: false }).limit(5),
        supabase.from('clubs').select('*').eq('id', prof.fighters.club_id).single(),
      ])
      setFighter(prof.fighters)
      setSkills(sk.data || [])
      setCompetitions(comps.data || [])
      setCategories(cats.data || [])
      setTechniques(techs.data || [])
      setRecentCheckins(cis.data || [])
      setCardClub(clubRes.data || null)
      const { data: bdg } = await supabase.from('fighter_badges').select('*').eq('fighter_id', fighterId).order('awarded_at', { ascending: false })
      setBadges(bdg || [])
      getFighterPoints(fighterId).then(setTotalPoints)
      setLoading(false)
    }
    load()
  }, [user, profile])

  if (loading) return <Layout><p style={{ color: '#888', textAlign: 'center', padding: '2rem' }}>Loading your card...</p></Layout>
  if (!fighter) return (
    <Layout>
      <div style={{ textAlign: 'center', padding: '3rem 1rem' }}>
        <div style={{ fontSize: 48, marginBottom: 12 }}>🥋</div>
        <p style={{ color: '#888', fontSize: 14 }}>No fighter profile linked to your account yet. Ask your coach to link your profile.</p>
      </div>
    </Layout>
  )

  const belt = getBelt(fighter)
  const beltLevel = getBeltLevel(fighter)
  const stripes = getStripeCount(fighter, beltLevel)
  const theme = getBeltTheme(fighter)

  const rating = calcRating(fighter, skills, competitions)

  const frontStats = FRONT_STAT_KEYS.map(({ label, cat }) => {
    const catData = categories.find(c => c.name === cat)
    const techIds = catData ? techniques.filter(t => t.category_id === catData.id).map(t => t.id) : []
    return { label, value: calcCatScore(skills, techIds) }
  })

  const backStats = categories.map(cat => {
    const techIds = techniques.filter(t => t.category_id === cat.id).map(t => t.id)
    return { name: cat.name, value: calcCatScore(skills, techIds) }
  })

  const faceHtml = fighter.photo_url
    ? null
    : fighter.avatar_style === 'initials'
    ? makeInitialsSvg(fighter.first_name, fighter.last_name, belt.a1, belt.a2, 200)
    : makeAvatarSvg(fighter.avatar_seed || 1, belt.a1, belt.a2, 200)

  const flagSvg = getFlagSvg(fighter.country || 'HR')
  const goldCount = competitions.filter(c => c.medal === 'gold').length
  const silverCount = competitions.filter(c => c.medal === 'silver').length
  const bronzeCount = competitions.filter(c => c.medal === 'bronze').length

  return (
    <Layout>
      <style>{`@keyframes shine{0%{left:-80%}60%{left:130%}100%{left:130%}} @import url('https://fonts.googleapis.com/css2?family=VT323&display=swap');`}</style>

      {/* Card */}
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '2rem' }}>
        <div onClick={() => setFlipped(f => !f)}
          style={{ width: 240, height: 380, perspective: 900, cursor: 'pointer' }}>
          <div style={{
            width: '100%', height: '100%', position: 'relative',
            transformStyle: 'preserve-3d',
            transition: 'transform 0.55s cubic-bezier(0.4,0,0.2,1)',
            transform: flipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
          }}>
            {/* FRONT */}
            <div style={{
              position: 'absolute', width: '100%', height: '100%', borderRadius: 16,
              overflow: 'hidden', backfaceVisibility: 'hidden',
              background: theme.bg, border: `3px solid ${theme.border}`, color: theme.color,
              display: 'flex', flexDirection: 'column',
            }}>
              <Shine />
              {/* Left vertical shade */}
              <div style={{ position: 'absolute', top: 0, left: 0, width: '38%', height: '100%', background: `linear-gradient(to right, rgba(0,0,0,0.12) 0%, rgba(0,0,0,0.06) 50%, transparent 100%)`, zIndex: 1, pointerEvents: 'none' }} />

              {/* Top-left overlay: rating, BJJ, flag, logo */}
              <div style={{ position: 'absolute', top: 8, left: 8, textAlign: 'center', lineHeight: 1, zIndex: 3 }}>
                <div style={{ fontSize: 56, fontWeight: 900, letterSpacing: '-2px', fontFamily: STENCIL_FONT, lineHeight: 1 }}>{rating}</div>
                <div style={{ fontSize: 13, letterSpacing: '0.12em', opacity: 0.85, fontFamily: STENCIL_FONT }}>BJJ</div>
                <div style={{ width: 28, height: '1px', background: 'currentColor', opacity: 0.3, margin: '2px auto' }} />
                <div style={{ fontSize: 24, lineHeight: 1, margin: '2px auto' }}>
                  {fighter.country === 'HR' ? '🇭🇷' : fighter.country === 'DE' ? '🇩🇪' : fighter.country === 'US' ? '🇺🇸' : fighter.country === 'GB' ? '🇬🇧' : fighter.country === 'FR' ? '🇫🇷' : fighter.country === 'IT' ? '🇮🇹' : fighter.country === 'ES' ? '🇪🇸' : fighter.country === 'BR' ? '🇧🇷' : fighter.country === 'JP' ? '🇯🇵' : fighter.country === 'AU' ? '🇦🇺' : '🏳️'}
                </div>
                {(cardClub || club)?.logo_url
                  ? <img src={(cardClub || club).logo_url} alt="" style={{ width: 44, height: 44, borderRadius: 6, objectFit: 'contain', background: 'transparent', display: 'block', margin: '3px auto 0', padding: 2, mixBlendMode: 'multiply' }} />
                  : <div style={{ width: 44, height: 44, borderRadius: 6, background: 'rgba(0,0,0,0.2)', border: '1.5px solid rgba(255,255,255,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 800, margin: '3px auto 0' }}>
                      {((cardClub || club)?.name || 'C')[0].toUpperCase()}
                    </div>
                }
              </div>

              {/* Photo fills remaining space */}
              <div style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
                {fighter.photo_url
                  ? <img src={fighter.photo_url} alt={fighter.first_name} style={{ width: '100%', height: '100%', objectFit: 'contain', objectPosition: 'top center', display: 'block' }} />
                  : <div dangerouslySetInnerHTML={{ __html: faceHtml }} style={{ width: '100%', height: '100%' }} />
                }
              </div>

              {/* Bottom: name + stats always at bottom */}
              <div style={{ padding: '5px 10px 8px', position: 'relative', zIndex: 2, flexShrink: 0 }}>
                <div style={{ fontSize: 16, fontWeight: 900, textAlign: 'center', letterSpacing: '0.08em', marginBottom: 4, fontFamily: STENCIL_FONT }}>
                  {fighter.first_name.toUpperCase()} {fighter.last_name.toUpperCase()}
                </div>
                <div style={{ height: '1px', background: `linear-gradient(to right, transparent, ${theme.border}, transparent)`, marginBottom: 5, opacity: 0.6 }} />
                <div style={{ display: 'flex', justifyContent: 'space-around' }}>
                  {frontStats.map(s => (
                    <div key={s.label} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                      <span style={{ fontSize: 18, fontWeight: 900, lineHeight: 1, fontFamily: STENCIL_FONT }}>{s.value}</span>
                      <span style={{ fontSize: 9, letterSpacing: '0.08em', opacity: 0.75, textTransform: 'uppercase', fontFamily: STENCIL_FONT }}>{s.label}</span>
                    </div>
                  ))}
                </div>
                {(goldCount + silverCount + bronzeCount) > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'center', gap: 4, marginTop: 4 }}>
                    {Array(Math.min(goldCount, 4)).fill(0).map((_, i) => <Pip key={'g'+i} color="#d4a017" />)}
                    {Array(Math.min(silverCount, 3)).fill(0).map((_, i) => <Pip key={'s'+i} color="#909090" />)}
                    {Array(Math.min(bronzeCount, 3)).fill(0).map((_, i) => <Pip key={'b'+i} color="#a05020" />)}
                  </div>
                )}
              </div>
            </div>

            {/* BACK */}
            <div style={{
              position: 'absolute', width: '100%', height: '100%', borderRadius: 16,
              overflow: 'hidden', backfaceVisibility: 'hidden', transform: 'rotateY(180deg)',
              background: theme.bg, border: `3px solid ${theme.border}`, color: theme.color
            }}>
              <Shine delay="2s" />
              <div style={{ padding: '12px 12px 10px', height: '100%', display: 'flex', flexDirection: 'column' }}>
                <div style={{ fontSize: 11, fontWeight: 700, textAlign: 'center', letterSpacing: '0.05em', marginBottom: 2 }}>
                  {fighter.first_name.toUpperCase()} {fighter.last_name.toUpperCase()}
                </div>
                <div style={{ fontSize: 9, textAlign: 'center', opacity: 0.6, marginBottom: 7 }}>
                  {belt.n}{stripes > 0 ? ' · ' + stripes + (stripes === 1 ? ' stripe' : ' stripes') : ''} · {fighter.sessions_count} sessions
                </div>
                <div style={{ height: '0.5px', background: 'currentColor', opacity: 0.2, marginBottom: 7 }} />
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 3.5 }}>
                  {backStats.map(s => (
                    <div key={s.name} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                      <span style={{ fontSize: 8, opacity: 0.7, flex: '0 0 62px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.name}</span>
                      <div style={{ flex: 1, height: 4, background: 'rgba(0,0,0,0.18)', borderRadius: 2, overflow: 'hidden' }}>
                        <div style={{ width: `${s.value}%`, height: '100%', background: 'currentColor', opacity: 0.75, borderRadius: 2 }} />
                      </div>
                      <span style={{ fontSize: 8.5, fontWeight: 700, opacity: 0.85, minWidth: 16, textAlign: 'right' }}>{s.value}</span>
                    </div>
                  ))}
                </div>
                {badges.length > 0 && (
                  <div style={{ marginTop: 8 }}>
                    <div style={{ fontSize: 8, opacity: 0.55, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Badges · {totalPoints} pts</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                      {badges.slice(0, 8).map(b => (
                        <span key={b.id} title={b.badge_name} style={{ fontSize: 14 }}>{b.badge_icon}</span>
                      ))}
                    </div>
                  </div>
                )}
                <div style={{ marginTop: 8 }}>
                  <div style={{ fontSize: 8, opacity: 0.55, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Competition history</div>
                  {competitions.length === 0
                    ? <div style={{ fontSize: 8.5, opacity: 0.45 }}>No competitions yet</div>
                    : competitions.slice(0, 3).map((c, i) => {
                        const col = c.medal === 'gold' ? '#d4a017' : c.medal === 'silver' ? '#909090' : '#a05020'
                        return (
                          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 3 }}>
                            <div style={{ width: 6, height: 6, borderRadius: '50%', background: col, flexShrink: 0 }} />
                            <span style={{ fontSize: 8, opacity: 0.85, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {c.tournament_name}{c.submission_used ? ' · ' + c.submission_used : ''}
                            </span>
                          </div>
                        )
                      })
                  }
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <p style={{ textAlign: 'center', fontSize: 12, color: '#aaa', marginBottom: '1rem' }}>Tap card to flip</p>

      <div style={{ display: 'flex', gap: 10, justifyContent: 'center', marginBottom: '2rem' }}>
        <button onClick={() => setSharing(true)} style={{
          background: 'linear-gradient(135deg,#5040b8,#3c30a0)',
          color: '#fff', border: 'none', borderRadius: 10,
          padding: '11px 24px', fontSize: 14, fontWeight: 700,
          cursor: 'pointer', fontFamily: 'inherit'
        }}>Share my card</button>
        <button onClick={async () => {
          if (rankRequestSent) return
          await supabase.from('promotion_requests').insert({
            fighter_id: fighter.id,
            club_id: fighter.club_id,
            current_belt_index: fighter.belt_index
          })
          setRankRequestSent(true)
        }} style={{
          background: rankRequestSent ? '#eaf7f0' : '#fff8e8',
          color: rankRequestSent ? '#1a7040' : '#a06000',
          border: `1px solid ${rankRequestSent ? '#b0e8cc' : '#f8d88a'}`,
          borderRadius: 10, padding: '11px 20px', fontSize: 13,
          fontWeight: 600, cursor: rankRequestSent ? 'default' : 'pointer',
          fontFamily: 'inherit'
        }}>{rankRequestSent ? 'Request sent!' : 'Request promotion'}</button>
      </div>

      {sharing && (
        <ShareCard fighter={fighter} skills={skills} competitions={competitions}
          categories={categories} techniques={techniques} onClose={() => setSharing(false)} />
      )}

      {/* Photo upload */}
      <div style={{ background: '#fff', borderRadius: 12, padding: '1.25rem', border: '1px solid #eee', marginBottom: '1rem' }}>
        <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 12, color: '#1a1a1a' }}>My photo</div>
        <PhotoUpload fighter={fighter} onUploaded={(url) => { setFighter(f => ({ ...f, photo_url: url, avatar_style: url ? 'photo' : 'avatar' })) }} />
      </div>

      {/* Personal data */}
      <PersonalDataSection fighter={fighter} onSaved={(data) => setFighter(f => ({ ...f, ...data }))} />

      {/* Stats row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: '1rem' }}>
        {[
          { label: 'Sessions', value: fighter.sessions_count },
          { label: 'Belt', value: stripes > 0 ? belt.n + ' (' + stripes + ')' : belt.n },
          { label: 'Medals', value: goldCount + silverCount + bronzeCount },
        ].map(s => (
          <div key={s.label} style={{ background: '#fff', borderRadius: 12, padding: '12px', textAlign: 'center', border: '1px solid #eee' }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#1a1a1a' }}>{s.value}</div>
            <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Membership */}
      <MembershipSection fighter={fighter} />

      {/* Recent sessions */}
      {recentCheckins.length > 0 && (
        <div style={{ background: '#fff', borderRadius: 12, padding: '1rem 1.25rem', border: '1px solid #eee', marginBottom: '1rem' }}>
          <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 10, color: '#1a1a1a' }}>Recent sessions</div>
          {recentCheckins.map(ci => (
            <div key={ci.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', borderBottom: '1px solid #f4f4f4' }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#1860a8', flexShrink: 0 }} />
              <div style={{ flex: 1, fontSize: 13, color: '#333' }}>{ci.classes?.name || 'Session'}</div>
              <div style={{ fontSize: 11, color: '#aaa' }}>{new Date(ci.checked_in_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</div>
            </div>
          ))}
        </div>
      )}

      {/* Competitions */}
      {competitions.length > 0 && (
        <div style={{ background: '#fff', borderRadius: 12, padding: '1rem 1.25rem', border: '1px solid #eee', marginBottom: '1rem' }}>
          <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 10, color: '#1a1a1a' }}>Competitions</div>
          {competitions.map((c, i) => {
            const col = c.medal === 'gold' ? '#d4a017' : c.medal === 'silver' ? '#909090' : '#a05020'
            return (
              <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0', borderBottom: i < competitions.length - 1 ? '1px solid #f4f4f4' : 'none' }}>
                <div style={{ width: 12, height: 12, borderRadius: '50%', background: col, flexShrink: 0 }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#1a1a1a' }}>{c.tournament_name}</div>
                  {c.submission_used && <div style={{ fontSize: 11, color: '#888' }}>via {c.submission_used}</div>}
                </div>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#1a1a1a', textTransform: 'capitalize' }}>{c.medal}</div>
              </div>
            )
          })}
        </div>
      )}

      {/* Passport ID */}
      {fighter.passport_id && (
        <div style={{ background: '#fff', borderRadius: 12, padding: '1rem 1.25rem', border: '1px solid #eee', marginBottom: '1rem' }}>
          <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 8, color: '#1a1a1a' }}>Sport passport</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
            <div style={{ fontFamily: 'monospace', fontSize: 16, fontWeight: 700, color: '#1860a8', letterSpacing: '0.1em', flex: 1 }}>
              {fighter.passport_id}
            </div>
            <button onClick={() => { navigator.clipboard.writeText(fighter.passport_id); alert('Passport ID copied!') }}
              style={{ background: 'transparent', border: '1px solid #ddd', borderRadius: 7, padding: '4px 10px', fontSize: 11, cursor: 'pointer', color: '#888' }}>
              Copy
            </button>
          </div>
          <div style={{ fontSize: 11, color: '#aaa', marginBottom: 12 }}>
            Share this ID with a new gym to transfer your full record, or with your parent to link their account.
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px', background: '#f8f9fc', borderRadius: 8 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 500, color: '#1a1a1a' }}>Public profile</div>
              <div style={{ fontSize: 11, color: '#888' }}>Allow search engines to find your card</div>
            </div>
            <button onClick={async () => {
              const newVal = !fighter.profile_public
              await supabase.from('fighters').update({ profile_public: newVal }).eq('id', fighter.id)
              setFighter(f => ({ ...f, profile_public: newVal }))
            }} style={{
              width: 44, height: 24, borderRadius: 12, border: 'none', cursor: 'pointer',
              background: fighter.profile_public ? '#1860a8' : '#ccc',
              position: 'relative', transition: 'background 0.2s', flexShrink: 0
            }}>
              <div style={{
                width: 18, height: 18, borderRadius: '50%', background: '#fff',
                position: 'absolute', top: 3, transition: 'left 0.2s',
                left: fighter.profile_public ? 23 : 3
              }} />
            </button>
          </div>
          {fighter.profile_public && (
            <div style={{ marginTop: 8, fontSize: 11, color: '#1860a8' }}>
              Public URL: {window.location.origin}/fighter/{fighter.passport_id}
            </div>
          )}
        </div>
      )}

      {/* Payment history */}
      <PaymentHistorySection fighter={fighter} />

      {/* Submit competition result */}
      <div style={{ background: '#fff', borderRadius: 12, padding: '1rem 1.25rem', border: '1px solid #eee', marginBottom: '1rem' }}>
        <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4, color: '#1a1a1a' }}>Competed recently?</div>
        <div style={{ fontSize: 12, color: '#888', marginBottom: 10 }}>Submit your result — your coach will review and add it to your card.</div>
        <CompetitionRequestForm fighter={fighter} />
      </div>

      {/* Request data correction */}
      <div style={{ background: '#fff', borderRadius: 12, padding: '1rem 1.25rem', border: '1px solid #eee', marginBottom: '1rem' }}>
        <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4, color: '#1a1a1a' }}>Spotted an error?</div>
        <div style={{ fontSize: 12, color: '#888', marginBottom: 10 }}>Request a correction from your coach — only they can edit your record.</div>
        <CorrectionRequestForm fighter={fighter} />
      </div>
    </Layout>
  )
}

// ── Personal Data Section ──────────────────────────────────────
function PersonalDataSection({ fighter, onSaved }) {
  const [open, setOpen] = useState(false)
  const [data, setData] = useState({
    phone: fighter.phone || '',
    date_of_birth: fighter.date_of_birth || '',
    address: fighter.address || '',
    emergency_contact: fighter.emergency_contact || '',
    emergency_phone: fighter.emergency_phone || '',
  })
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const set = (k, v) => setData(d => ({ ...d, [k]: v }))

  async function save() {
    setSaving(true)
    await supabase.from('fighters').update(data).eq('id', fighter.id)
    setSaving(false)
    setSaved(true)
    onSaved(data)
    setTimeout(() => setSaved(false), 2000)
  }

  return (
    <div style={{ background: '#fff', borderRadius: 12, padding: '1rem 1.25rem', border: '1px solid #eee', marginBottom: '1rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: open ? 14 : 0 }}>
        <div style={{ fontWeight: 600, fontSize: 14, color: '#1a1a1a', flex: 1 }}>My details</div>
        <button onClick={() => setOpen(o => !o)} style={{ background: 'none', border: '1px solid #ddd', borderRadius: 7, padding: '4px 12px', fontSize: 12, cursor: 'pointer', color: '#666', fontFamily: 'inherit' }}>
          {open ? 'Close' : 'Edit'}
        </button>
      </div>
      {!open && (
        <div style={{ fontSize: 12, color: '#aaa', marginTop: 4 }}>
          {[fighter.phone, fighter.date_of_birth, fighter.address].filter(Boolean).join(' · ') || 'Tap Edit to add your details'}
        </div>
      )}
      {open && (
        <div>
          <Field label="Phone number">
            <input value={data.phone} onChange={e => set('phone', e.target.value)}
              placeholder="+385 91 234 5678" type="tel"
              style={inputStyle} />
          </Field>
          <Field label="Date of birth">
            <input value={data.date_of_birth} onChange={e => set('date_of_birth', e.target.value)}
              type="date" style={inputStyle} />
          </Field>
          <Field label="Address">
            <input value={data.address} onChange={e => set('address', e.target.value)}
              placeholder="Ilica 1, Zagreb" style={inputStyle} />
          </Field>
          <Field label="Emergency contact name">
            <input value={data.emergency_contact} onChange={e => set('emergency_contact', e.target.value)}
              placeholder="Ana Horvat" style={inputStyle} />
          </Field>
          <Field label="Emergency contact phone">
            <input value={data.emergency_phone} onChange={e => set('emergency_phone', e.target.value)}
              placeholder="+385 91 000 0000" type="tel" style={inputStyle} />
          </Field>
          <button onClick={save} disabled={saving} style={{
            background: '#1860a8', color: '#fff', border: 'none', borderRadius: 8,
            padding: '9px 18px', fontSize: 13, cursor: 'pointer', fontFamily: 'inherit', fontWeight: 500, marginTop: 4
          }}>
            {saved ? '✓ Saved' : saving ? 'Saving...' : 'Save details'}
          </button>
        </div>
      )}
    </div>
  )
}

// ── Membership Section ─────────────────────────────────────────
function MembershipSection({ fighter }) {
  const [sub, setSub] = useState(null)
  const [plans, setPlans] = useState([])
  const [loadingData, setLoadingData] = useState(true)
  const [selectedPlanId, setSelectedPlanId] = useState('')
  const [paying, setPaying] = useState(false)
  const [portalLoading, setPortalLoading] = useState(false)
  const [discountCode, setDiscountCode] = useState('')
  const [discountApplied, setDiscountApplied] = useState(null)
  const [discountError, setDiscountError] = useState('')
  const [showChangePlan, setShowChangePlan] = useState(false)
  const [resubscribeTiming, setResubscribeTiming] = useState('now') // 'now' or 'after'
  const [cancelling, setCancelling] = useState(false)
  const [newPlanId, setNewPlanId] = useState('')

  useEffect(() => {
    async function load() {
      const [subRes, plansRes] = await Promise.all([
        supabase.from('member_subscriptions').select('*, membership_plans(*)').eq('fighter_id', fighter.id).maybeSingle(),
        supabase.from('membership_plans').select('*').eq('club_id', fighter.club_id).eq('active', true),
      ])
      setSub(subRes.data)
      setPlans(plansRes.data || [])
      if (subRes.data?.plan_id) {
        setSelectedPlanId(subRes.data.plan_id)
        setNewPlanId(subRes.data.status === 'cancelled' ? '' : subRes.data.plan_id)
      }
      setLoadingData(false)
    }
    load()
  }, [fighter.id, fighter.club_id])

  async function pay() {
    const planId = sub?.plan_id || selectedPlanId
    if (!planId) return
    setPaying(true)
    const { data, error } = await supabase.functions.invoke('create-checkout', {
      body: {
        fighter_id: fighter.id,
        plan_id: planId,
        success_url: window.location.href,
        cancel_url: window.location.href,
        discount_code: discountCode || undefined,
      }
    })
    if (data?.url) window.location.href = data.url
    else { alert('Payment error: ' + (error?.message || 'Please try again')); setPaying(false) }
  }

  async function applyDiscount() {
    if (!discountCode.trim()) return
    setDiscountError('')
    const { data: code } = await supabase.from('discount_codes').select('*')
      .eq('club_id', fighter.club_id).eq('code', discountCode.toUpperCase().trim()).eq('active', true).single()
    if (!code) { setDiscountError('Invalid or expired code'); return }
    if (code.expires_at && new Date(code.expires_at) < new Date()) { setDiscountError('This code has expired'); return }
    if (code.max_uses !== null && code.uses_count >= code.max_uses) { setDiscountError('This code is fully used'); return }
    const plan = plans.find(p => p.id === (sub?.plan_id || selectedPlanId))
    const planAmount = plan?.amount || 0
    const discounted = code.type === 'percent'
      ? Math.round(planAmount * (1 - code.amount / 100))
      : Math.max(0, planAmount - code.amount)
    setDiscountApplied({ code, discounted })
  }

  async function openPortal() {
    setPortalLoading(true)
    const { data, error } = await supabase.functions.invoke('customer-portal', {
      body: { fighter_id: fighter.id, return_url: window.location.href }
    })
    if (data?.url) window.location.href = data.url
    else { alert('Error: ' + (error?.message || 'Could not open billing portal')); setPortalLoading(false) }
  }

  async function cancelMembership() {
    if (!window.confirm('Are you sure you want to cancel your membership? It will remain active until the end of the current billing period.')) return
    setCancelling(true)
    const accessUntil = sub.next_billing_date || null
    await supabase.from('member_subscriptions').update({
      status: 'cancelled',
      cancelled_at: new Date().toISOString(),
      access_until: accessUntil,
    }).eq('id', sub.id)
    setSub(prev => ({ ...prev, status: 'cancelled', cancelled_at: new Date().toISOString(), access_until: accessUntil }))
    setCancelling(false)
  }

  async function changePlan() {
    if (!newPlanId) { setShowChangePlan(false); return }
    if (sub.status === 'cancelled') {
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: { plan_id: newPlanId, fighter_id: fighter.id, success_url: window.location.href + '?subscribed=true', cancel_url: window.location.href, trial_end: resubscribeTiming === 'after' && (sub.access_until || sub.next_billing_date) ? Math.floor(new Date(sub.access_until || sub.next_billing_date).getTime() / 1000) : null }
      })
      if (data?.url) { window.location.href = data.url; return }
      alert('Error: ' + (error?.message || 'Could not start checkout'))
      return
    }
    if (newPlanId === sub.plan_id && sub.status !== 'cancelled') { setShowChangePlan(false); return }
    await supabase.from('member_subscriptions').update({
      plan_id: newPlanId,
      updated_at: new Date().toISOString(),
    }).eq('id', sub.id)
    const newPlan = plans.find(p => p.id === newPlanId)
    setSub(prev => ({ ...prev, plan_id: newPlanId, membership_plans: newPlan }))
    setShowChangePlan(false)
    alert('Plan changed successfully! The new plan will take effect on your next billing date.')
  }

  function formatDate(dateStr) {
    if (!dateStr) return null
    const d = new Date(dateStr)
    if (isNaN(d)) return null
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
  }

  if (loadingData) return null

  // ── No subscription yet — show plan picker ──
  if (!sub) {
    if (plans.length === 0) return null
    return (
      <div style={{ background: '#fff', borderRadius: 12, padding: '1rem 1.25rem', border: '1px solid #eee', marginBottom: '1rem' }}>
        <div style={{ fontWeight: 600, fontSize: 14, color: '#1a1a1a', marginBottom: 4 }}>Choose a membership</div>
        <div style={{ fontSize: 12, color: '#888', marginBottom: 14 }}>Select a plan to get started</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 }}>
          {plans.map(p => (
            <div key={p.id} onClick={() => setSelectedPlanId(p.id)}
              style={{
                border: `2px solid ${selectedPlanId === p.id ? '#1860a8' : '#eee'}`,
                borderRadius: 10, padding: '12px 14px', cursor: 'pointer',
                background: selectedPlanId === p.id ? '#f0f6ff' : '#fff',
                transition: 'all 0.15s'
              }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{
                  width: 18, height: 18, borderRadius: '50%', border: `2px solid ${selectedPlanId === p.id ? '#1860a8' : '#ddd'}`,
                  background: selectedPlanId === p.id ? '#1860a8' : '#fff', flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}>
                  {selectedPlanId === p.id && <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#fff' }} />}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#1a1a1a' }}>{p.name}</div>
                  {p.description && <div style={{ fontSize: 12, color: '#888', marginTop: 1 }}>{p.description}</div>}
                </div>
                <div style={{ fontSize: 15, fontWeight: 700, color: '#1860a8' }}>
                  {(p.amount / 100).toFixed(2)} {p.currency?.toUpperCase()}
                  <div style={{ fontSize: 10, fontWeight: 400, color: '#888', textAlign: 'right' }}>/ {p.interval}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
        {selectedPlanId && (
          <button onClick={pay} disabled={paying} style={{
            width: '100%', padding: '12px', borderRadius: 10, border: 'none',
            background: '#1860a8', color: '#fff', fontWeight: 700, fontSize: 15,
            cursor: paying ? 'not-allowed' : 'pointer', fontFamily: 'inherit'
          }}>
            {paying ? 'Redirecting to payment...' : 'Continue to payment →'}
          </button>
        )}
      </div>
    )
  }

  // ── Has subscription — show status ──
  const accessUntilDate = sub.access_until || sub.next_billing_date
  const isExpired = !accessUntilDate || new Date(accessUntilDate) <= new Date()
  const statusColor = { active: '#1a7040', overdue: '#a06000', frozen: '#3050a8', suspended: '#c00', cancelled: isExpired ? '#c00' : '#888', pending: '#a06000' }[sub.status] || '#888'
  const statusBg = { active: '#eaf7f0', overdue: '#fff8e8', frozen: '#e8f0ff', suspended: '#fff0f0', cancelled: isExpired ? '#fff0f0' : '#f8f8f8', pending: '#fff8e8' }[sub.status] || '#f8f8f8'

  return (
    <div style={{ background: '#fff', borderRadius: 12, padding: '1rem 1.25rem', border: `1px solid ${sub.status === 'suspended' ? '#fcc' : sub.status === 'overdue' ? '#f8d88a' : '#eee'}`, marginBottom: '1rem' }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: '#1a1a1a' }}>{sub.membership_plans?.name}</div>
          <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>
            {sub.membership_plans ? `${(sub.membership_plans.amount / 100).toFixed(2)} ${sub.membership_plans.currency?.toUpperCase()} / ${sub.membership_plans.interval}` : ''}
          </div>
        </div>
        <span style={{ fontSize: 11, padding: '3px 10px', borderRadius: 20, background: statusBg, color: statusColor, fontWeight: 500 }}>
          {sub.status === 'cancelled' && isExpired ? 'No active membership' : sub.status.charAt(0).toUpperCase() + sub.status.slice(1)}
        </span>
      </div>

      {/* Duration info */}
      <div style={{ background: '#f8f9fc', borderRadius: 8, padding: '10px 12px', marginBottom: 12, display: 'flex', flexDirection: 'column', gap: 6 }}>
        {sub.start_date && (
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
            <span style={{ color: '#888' }}>Member since</span>
            <span style={{ color: '#1a1a1a', fontWeight: 500 }}>{formatDate(sub.start_date)}</span>
          </div>
        )}
        {sub.next_billing_date && sub.status === 'active' && (
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
            <span style={{ color: '#888' }}>Next billing</span>
            <span style={{ color: '#1a1a1a', fontWeight: 500 }}>{formatDate(sub.next_billing_date)}</span>
          </div>
        )}
        {sub.status === 'cancelled' && !showChangePlan && (
          <button onClick={() => { setShowChangePlan(true); setResubscribeTiming('now') }} style={{ width: '100%', padding: '10px', background: '#1860a8', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', marginBottom: 10 }}>Choose a new plan</button>
        )}
        {sub.status === 'cancelled' && sub.cancelled_at && (
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
            <span style={{ color: '#888' }}>Cancelled on</span>
            <span style={{ color: '#c00', fontWeight: 500 }}>{formatDate(sub.cancelled_at)}</span>
          </div>
        )}
        {sub.next_billing_date && sub.status === 'cancelled' && new Date(sub.next_billing_date) > new Date() && (
          <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>
            Access until {formatDate(sub.next_billing_date)}
          </div>
        )}
      </div>

      {/* Change plan section */}
      {showChangePlan && (sub.status === 'active' || sub.status === 'cancelled') && (
        <div style={{ background: '#f0f6ff', borderRadius: 10, padding: '12px', marginBottom: 12, border: '1px solid #c8d8f8' }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#1860a8', marginBottom: 8 }}>Change plan</div>
          <div style={{ fontSize: 11, color: '#888', marginBottom: 10 }}>The new plan takes effect on your next billing date.</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 10 }}>
            {plans.map(p => (
              <div key={p.id} onClick={() => setNewPlanId(p.id)}
                style={{
                  border: `2px solid ${newPlanId === p.id ? '#1860a8' : '#ddd'}`,
                  borderRadius: 8, padding: '10px 12px', cursor: 'pointer',
                  background: newPlanId === p.id ? '#e8f0ff' : '#fff',
                }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#1a1a1a' }}>{p.name}</div>
                    {p.id === sub.plan_id && sub.status !== 'cancelled' && <div style={{ fontSize: 10, color: '#1860a8' }}>Current plan</div>}
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#1860a8' }}>
                    {(p.amount / 100).toFixed(2)} {p.currency?.toUpperCase()}/{p.interval}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={changePlan} disabled={sub.status !== 'cancelled' ? newPlanId === sub.plan_id : false} style={{
              background: '#1860a8', color: '#fff', border: 'none', borderRadius: 8,
              padding: '8px 16px', fontSize: 13, cursor: newPlanId === sub.plan_id ? 'not-allowed' : 'pointer',
              fontFamily: 'inherit', opacity: newPlanId === sub.plan_id ? 0.5 : 1,
            }}>{sub.status === 'cancelled' ? 'Subscribe' : 'Confirm change'}</button>
            <button onClick={() => { setShowChangePlan(false); setNewPlanId(sub.plan_id) }} style={{
              background: 'transparent', color: '#888', border: '1px solid #ddd', borderRadius: 8,
              padding: '8px 14px', fontSize: 13, cursor: 'pointer', fontFamily: 'inherit',
            }}>Cancel</button>
          </div>
        </div>
      )}

      {/* Warnings */}
      {sub.status === 'suspended' && (
        <div style={{ background: '#fff0f0', borderRadius: 8, padding: '10px 12px', marginBottom: 10, fontSize: 13, color: '#c00', lineHeight: 1.5 }}>
          Your membership has been suspended due to a failed payment. Update your payment method to reactivate.
        </div>
      )}
      {sub.status === 'overdue' && (
        <div style={{ background: '#fff8e8', borderRadius: 8, padding: '10px 12px', marginBottom: 10, fontSize: 13, color: '#a06000', lineHeight: 1.5 }}>
          Your payment failed. Please update your payment method to avoid suspension.
          {sub.payment_failed_count > 0 && ` (${sub.payment_failed_count} attempt${sub.payment_failed_count !== 1 ? 's' : ''} failed)`}
        </div>
      )}

      {/* Discount code */}
      {(sub.status === 'overdue' || sub.status === 'pending' || sub.status === 'suspended') && (
        <div style={{ marginBottom: 10 }}>
          {discountApplied ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', background: '#eaf7f0', borderRadius: 8, border: '1px solid #b0e8cc' }}>
              <div style={{ flex: 1, fontSize: 12, color: '#1a7040' }}>
                Code <strong>{discountApplied.code.code}</strong> applied —
                {discountApplied.code.type === 'percent' ? ` ${discountApplied.code.amount}% off` : ` ${(discountApplied.code.amount / 100).toFixed(2)} off`}
                {' '}→ {(discountApplied.discounted / 100).toFixed(2)} {sub.membership_plans?.currency?.toUpperCase()}
              </div>
              <button onClick={() => { setDiscountApplied(null); setDiscountCode('') }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#1a7040', fontSize: 16 }}>×</button>
            </div>
          ) : (
            <div style={{ display: 'flex', gap: 8 }}>
              <input value={discountCode} onChange={e => { setDiscountCode(e.target.value.toUpperCase()); setDiscountError('') }}
                placeholder="Discount code"
                style={{ flex: 1, padding: '7px 10px', borderRadius: 8, border: '1px solid #ddd', fontSize: 13, fontFamily: 'monospace' }} />
              <button onClick={applyDiscount}
                style={{ background: 'transparent', border: '1px solid #ddd', borderRadius: 8, padding: '7px 12px', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit', color: '#444' }}>
                Apply
              </button>
            </div>
          )}
          {discountError && <div style={{ fontSize: 11, color: '#c00', marginTop: 4 }}>{discountError}</div>}
        </div>
      )}

      {/* Action buttons */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {(sub.status === 'overdue' || sub.status === 'pending' || sub.status === 'suspended') && (
          <button onClick={pay} disabled={paying} style={{
            width: '100%', padding: '10px', borderRadius: 9, border: 'none',
            background: sub.status === 'suspended' ? '#c00' : '#a06000',
            color: '#fff', fontWeight: 700, fontSize: 14,
            cursor: paying ? 'not-allowed' : 'pointer', fontFamily: 'inherit'
          }}>{paying ? 'Redirecting...' : 'Pay now'}</button>
        )}
        {sub.stripe_customer_id && (
          <button onClick={openPortal} disabled={portalLoading} style={{
            width: '100%', padding: '9px', borderRadius: 9,
            background: 'transparent', border: '1px solid #ddd',
            color: '#444', fontSize: 13, cursor: 'pointer', fontFamily: 'inherit'
          }}>{portalLoading ? 'Opening...' : 'Update payment method'}</button>
        )}
        {sub.status === 'active' && !showChangePlan && (
          <button onClick={() => setShowChangePlan(true)} style={{
            width: '100%', padding: '9px', borderRadius: 9,
            background: 'transparent', border: '1px solid #1860a8',
            color: '#1860a8', fontSize: 13, cursor: 'pointer', fontFamily: 'inherit'
          }}>Change plan</button>
        )}
        {sub.status === 'active' && (
          <button onClick={cancelMembership} disabled={cancelling} style={{
            width: '100%', padding: '9px', borderRadius: 9,
            background: 'transparent', border: '1px solid #fcc',
            color: '#c00', fontSize: 13, cursor: 'pointer', fontFamily: 'inherit'
          }}>{cancelling ? 'Cancelling...' : 'Cancel membership'}</button>
        )}
      </div>
    </div>
  )
}

function PaymentHistorySection({ fighter }) {
  const [payments, setPayments] = useState([])
  const [open, setOpen] = useState(false)

  async function load() {
    const { data } = await supabase.from('payment_history').select('*')
      .eq('fighter_id', fighter.id).order('paid_at', { ascending: false }).limit(10)
    setPayments(data || [])
    setOpen(true)
  }

  if (!open) return (
    <div style={{ marginBottom: '1rem' }}>
      <button onClick={load} style={{ background: 'transparent', border: '1px solid #ddd', borderRadius: 8, padding: '7px 14px', fontSize: 12, cursor: 'pointer', color: '#888', fontFamily: 'inherit' }}>
        View payment history
      </button>
    </div>
  )

  return (
    <div style={{ background: '#fff', borderRadius: 12, padding: '1rem 1.25rem', border: '1px solid #eee', marginBottom: '1rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
        <div style={{ fontWeight: 600, fontSize: 14, color: '#1a1a1a', flex: 1 }}>Payment history</div>
        <button onClick={() => setOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#aaa', fontSize: 18 }}>×</button>
      </div>
      {payments.length === 0
        ? <div style={{ fontSize: 13, color: '#aaa' }}>No payments yet.</div>
        : payments.map(p => (
          <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0', borderBottom: '1px solid #f4f4f4' }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: p.status === 'succeeded' ? '#1a7040' : '#c00', flexShrink: 0 }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, color: '#1a1a1a' }}>{p.paid_at ? new Date(p.paid_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : 'Pending'}</div>
              {p.notes && <div style={{ fontSize: 11, color: '#aaa' }}>{p.notes}</div>}
            </div>
            <div style={{ fontSize: 13, fontWeight: 600, color: p.status === 'succeeded' ? '#1a7040' : '#c00' }}>
              {(p.amount / 100).toFixed(2)} {p.currency?.toUpperCase()}
            </div>
          </div>
        ))
      }
    </div>
  )
}


function CompetitionRequestForm({ fighter }) {
  const [open, setOpen] = useState(false)
  const [sent, setSent] = useState(false)
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState({
    tournament_name: '', competition_date: new Date().toISOString().split('T')[0],
    medal: '', wins: '0', submission_used: '', notes: ''
  })
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  async function send() {
    if (!form.tournament_name.trim()) return
    setSaving(true)
    await supabase.from('competition_requests').insert({
      fighter_id: fighter.id,
      club_id: fighter.club_id,
      tournament_name: form.tournament_name.trim(),
      competition_date: form.competition_date || null,
      medal: form.medal || null,
      wins: parseInt(form.wins) || 0,
      submission_used: form.submission_used.trim() || null,
      notes: form.notes.trim() || null,
    })
    setSaving(false); setSent(true); setOpen(false)
  }

  if (sent) return <div style={{ fontSize: 13, color: '#1a7040' }}>Competition result submitted — waiting for your coach to approve it.</div>
  if (!open) return (
    <button onClick={() => setOpen(true)} style={{ background: 'transparent', border: '1px solid #ddd', borderRadius: 8, padding: '6px 14px', fontSize: 12, cursor: 'pointer', color: '#888', fontFamily: 'inherit' }}>
      Submit a competition result
    </button>
  )

  return (
    <div>
      <input value={form.tournament_name} onChange={e => set('tournament_name', e.target.value)}
        placeholder="Tournament name"
        style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #ddd', fontSize: 13, marginBottom: 8, boxSizing: 'border-box' }} />
      <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
        <input type="date" value={form.competition_date} onChange={e => set('competition_date', e.target.value)}
          style={{ flex: 1, padding: '8px 10px', borderRadius: 8, border: '1px solid #ddd', fontSize: 13, boxSizing: 'border-box' }} />
        <select value={form.medal} onChange={e => set('medal', e.target.value)}
          style={{ flex: 1, padding: '8px 10px', borderRadius: 8, border: '1px solid #ddd', fontSize: 13, background: '#fff' }}>
          <option value="">No medal / points</option>
          <option value="gold">🥇 Gold</option>
          <option value="silver">🥈 Silver</option>
          <option value="bronze">🥉 Bronze</option>
        </select>
      </div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
        <input type="number" value={form.wins} onChange={e => set('wins', e.target.value)}
          placeholder="Wins" min="0"
          style={{ flex: 1, padding: '8px 10px', borderRadius: 8, border: '1px solid #ddd', fontSize: 13, boxSizing: 'border-box' }} />
        <input value={form.submission_used} onChange={e => set('submission_used', e.target.value)}
          placeholder="Submission used (optional)"
          style={{ flex: 2, padding: '8px 10px', borderRadius: 8, border: '1px solid #ddd', fontSize: 13, boxSizing: 'border-box' }} />
      </div>
      <textarea value={form.notes} onChange={e => set('notes', e.target.value)}
        placeholder="Notes for your coach (optional)"
        style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #ddd', fontSize: 13, minHeight: 60, resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box', marginBottom: 8 }} />
      <div style={{ display: 'flex', gap: 8 }}>
        <button onClick={send} disabled={saving || !form.tournament_name.trim()} style={{ background: '#1860a8', color: '#fff', border: 'none', borderRadius: 8, padding: '7px 14px', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' }}>
          {saving ? 'Sending...' : 'Submit for approval'}
        </button>
        <button onClick={() => setOpen(false)} style={{ background: 'transparent', border: '1px solid #ddd', borderRadius: 8, padding: '7px 14px', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit', color: '#888' }}>
          Cancel
        </button>
      </div>
    </div>
  )
}

function CorrectionRequestForm({ fighter }) {
  const [open, setOpen] = useState(false)
  const [type, setType] = useState('competition')
  const [desc, setDesc] = useState('')
  const [sent, setSent] = useState(false)
  const [saving, setSaving] = useState(false)

  async function send() {
    if (!desc.trim()) return
    setSaving(true)
    await supabase.from('correction_requests').insert({
      fighter_id: fighter.id, club_id: fighter.club_id,
      record_type: type, description: desc.trim(),
    })
    setSaving(false); setSent(true); setOpen(false); setDesc('')
  }

  if (sent) return <div style={{ fontSize: 13, color: '#1a7040' }}>Correction request sent to your coach.</div>
  if (!open) return (
    <button onClick={() => setOpen(true)} style={{ background: 'transparent', border: '1px solid #ddd', borderRadius: 8, padding: '6px 14px', fontSize: 12, cursor: 'pointer', color: '#888', fontFamily: 'inherit' }}>
      Request a correction
    </button>
  )

  return (
    <div>
      <select value={type} onChange={e => setType(e.target.value)}
        style={{ width: '100%', padding: '7px 10px', borderRadius: 8, border: '1px solid #ddd', fontSize: 12, marginBottom: 8 }}>
        <option value="competition">Competition result</option>
        <option value="belt">Belt rank</option>
        <option value="checkin">Session check-in</option>
        <option value="skill">Skill rating</option>
        <option value="other">Other</option>
      </select>
      <textarea value={desc} onChange={e => setDesc(e.target.value)}
        placeholder="Describe the error and what the correct information should be..."
        style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #ddd', fontSize: 12, minHeight: 70, resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box', marginBottom: 8 }}
      />
      <div style={{ display: 'flex', gap: 8 }}>
        <button onClick={send} disabled={saving} style={{ background: '#1860a8', color: '#fff', border: 'none', borderRadius: 8, padding: '7px 14px', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' }}>
          {saving ? 'Sending...' : 'Send request'}
        </button>
        <button onClick={() => setOpen(false)} style={{ background: 'transparent', border: '1px solid #ddd', borderRadius: 8, padding: '7px 14px', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit', color: '#888' }}>
          Cancel
        </button>
      </div>
    </div>
  )
}

function Layout({ children }) {
  const { signOut } = useAuth()
  async function handleSignOut() { await signOut(); window.location.replace('/') }
  return (
    <div style={{ minHeight: '100vh', background: '#f4f6fb', fontFamily: "'Segoe UI', system-ui, sans-serif" }}>
      <style>{`@keyframes shine{0%{left:-80%}60%{left:130%}100%{left:130%}} @import url('https://fonts.googleapis.com/css2?family=VT323&display=swap');`}</style>
      <header style={{ background: '#fff', borderBottom: '1px solid #eee', padding: '0 1.25rem', height: 52, display: 'flex', alignItems: 'center', gap: 10, position: 'sticky', top: 0, zIndex: 100 }}>
        <span style={{ fontSize: 20 }}>🥋</span>
        <span style={{ fontWeight: 700, fontSize: 15, color: '#1a1a1a', flex: 1 }}>My Fighter Card</span>
        <button onClick={handleSignOut} style={{ background: 'none', border: '1px solid #ddd', borderRadius: 8, padding: '5px 10px', fontSize: 12, color: '#888', cursor: 'pointer' }}>Sign out</button>
      </header>
      <main style={{ maxWidth: 480, margin: '0 auto', padding: '1.5rem 1rem' }}>{children}</main>
    </div>
  )
}

function Shine({ delay = '0s' }) {
  return (
    <div style={{
      position: 'absolute', top: 0, left: '-80%', width: '40%', height: '100%',
      background: 'linear-gradient(105deg,transparent 30%,rgba(255,255,255,0.22) 50%,transparent 70%)',
      animation: `shine 4s ${delay} infinite`, pointerEvents: 'none', zIndex: 10
    }} />
  )
}

function Pip({ color }) {
  return <div style={{ width: 9, height: 9, borderRadius: '50%', background: color }} />
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <label style={{ display: 'block', fontSize: 11, color: '#666', marginBottom: 4, fontWeight: 500 }}>{label}</label>
      {children}
    </div>
  )
}

const inputStyle = {
  width: '100%', padding: '8px 10px', borderRadius: 8,
  border: '1px solid #ddd', fontSize: 13, fontFamily: 'inherit',
  boxSizing: 'border-box'
}
